import { Button, Col, Row, Tooltip } from "antd"
import { cleanQuery, formatCep, formatCnae, formatCnpj, formatPhone, porteEmpresa, SearchResultData } from "../../router/SearchResultTable"
import Icons from "./Icons"
import posthog from "posthog-js"
import { subclasseSelect } from "../../config/data"
import { CiCircleInfo } from "react-icons/ci"
import DeleteIcon from '../../assets/svg/deleteIcon.svg'
import { useCrm } from "../CrmDropdown"
import { useAds } from "../AdsDropdown"
import { useAiv } from "../aivListDropdown"
import ConnectionButtonIcon from '../../assets/svg/connectionButtonIcon.svg'
import { FaUserFriends, FaBuilding } from "react-icons/fa"

interface LeadCardProps{
    lead: SearchResultData,
    setSelectedRecord: any,
    userPermissions:any,
    setApiResponse: any,
    setModalLoading:any,
    setIsModalVisible:any,
    setIsTechModalVisible: any,
    haveEnrichmentCredits: any,
    setDomainToEnrich: any,
    setEnrichmentModalVisible: any,
    setLeadToDelete: any,
    setIsDeleteModalVisible: any,
    individualSearch: boolean,
    searchQuery: string,
    path: string
}


function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface HighlightableTextProps {
  text: string;
  highlight?: string;
  color?: string;
}

export function HighlightableText({ text, highlight, color }: HighlightableTextProps){
  if(!text) return '';
  if (!highlight) return <span className={color === 'black' ? 'leadCardDataBlack' : color === 'gray' ? 'leadCardDataGray' : color === 'green' ? 'leadCardDataGreen' : "leadCardData"}>{text}</span>;

  const normalizedText = cleanQuery(text);
  const normalizedHighlight = cleanQuery(highlight);
  const safeHighlight = escapeRegex(normalizedHighlight);
  const regex = new RegExp(`(${safeHighlight})`, "gi");
  const parts = normalizedText.split(regex);
  let cursor = 0;

  return (
    <span className={color === 'black' ? 'leadCardDataBlack' : color === 'gray' ? 'leadCardDataGray' : color === 'green' ? 'leadCardDataGreen' : "leadCardData"}>
      {parts.map((part, i) => {
        const originalPart = text.slice(cursor, cursor + part.length);
        cursor += part.length;

        return regex.test(part) ? (
          <mark key={i} style={{ backgroundColor: "#33EA89", padding: 0}}>
            {originalPart}
          </mark>
        ) : (
          <span key={i}>
            {originalPart}
          </span>
        );
      })}
    </span>
  );
}

const normalizeCount = (value?: number | string) => {
    if (value === undefined || value === null || value === "") return undefined;
    const numericValue = typeof value === "string" ? Number(value.replace(",", ".")) : value;
    if (Number.isNaN(numericValue)) return undefined;
    return Math.trunc(numericValue);
};

const LeadCard: React.FC<LeadCardProps> = ({
        lead, 
        setSelectedRecord, 
        userPermissions, 
        setApiResponse, 
        setModalLoading, 
        setIsModalVisible, 
        setIsTechModalVisible, 
        haveEnrichmentCredits, 
        setDomainToEnrich, 
        setEnrichmentModalVisible,
        setLeadToDelete,
        setIsDeleteModalVisible,
        searchQuery,
        path
    })=>{
    const { sendToCrmButton, selectedCrm } = useCrm();
    const { sendToAdsButton, selectedAds } = useAds();
    const {sendToAivButton, selectedList} = useAiv()
    const connection = selectedCrm ?? selectedAds ?? selectedList;
    const sociosCount = normalizeCount(lead.qtd_socios);
    const establishmentsCount = normalizeCount(lead.qtd_estabelecimentos);
    const sociosList = String(lead.socios ?? "")
        .split(",")
        .map((socio) => socio.trim())
        .filter(Boolean);
    
    return(
        <Row style={{border: 'border: 2px solid #E6E6F1', backgroundColor:'#F5F5F5', borderRadius:'30px', marginBottom:'1em', overflow: 'hidden'}} onMouseEnter={()=>{localStorage.setItem("leadPath", path);}}>
            <Col className="leadCardCol" style={{backgroundColor: '#E6E6F1'}}>
                <Row >
                    <span className= "leadCardTitle">Razão social<br/>
                        <HighlightableText text={lead.razao_social} highlight={searchQuery} />
                    </span> 
                </Row>
                <Row>
                   <span className= "leadCardTitle">Nome Fantasia<br/>
                        <HighlightableText text={lead.nome_fantasia ? lead.nome_fantasia : '-'} highlight={searchQuery} />
                    </span>
                </Row>
                <Row>
                    <span className= "leadCardTitle">CNPJ <br/>
                        <HighlightableText text={formatCnpj(lead.cnpj_completo)} highlight={searchQuery} />
                    </span>
                </Row>
            </Col>
            <Col className="leadCardCol" style={{display: 'flex', flexDirection:'column', justifyContent: 'space-between'}}>
                <div>
                    <Row>
                        <Col>
                            <span className= "leadCardTitle">UF</span> <br/>
                            <HighlightableText text={lead.uf} highlight={searchQuery} />
                        </Col>
                        <Col style={{marginLeft:'1em'}}>
                            <span className= "leadCardTitle">Cidade</span> <br/>
                            <HighlightableText text={lead.municipio} highlight={searchQuery} />
                        </Col>
                    </Row>
                    <Row>
                        <span className= "leadCardTitle">Endereço <br/>
                        <HighlightableText text={`${lead.logradouro}, ${lead.bairro}, ${formatCep(lead.cep)}`} highlight={searchQuery} />
                        </span>
                    </Row>
                    <Row>
                        <span className="leadCardTitle" style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                            <FaUserFriends style={{ marginRight: '0.3em' }} />
                            <span className="leadCardData">
                                {
                                    sociosCount !== undefined
                                        ? `${sociosCount} sócio(s)`
                                        : (
                                            sociosList.length
                                                ? `${sociosList.length} sócio(s)`
                                                : "-"
                                        )
                                }
                            </span>
                        </span>
                        <span className="leadCardTitle" style={{ display: 'flex', alignItems: 'center' }}>
                            <FaBuilding style={{ marginRight: '0.3em' }} />
                            <span className="leadCardData">
                                {establishmentsCount !== undefined ? `${establishmentsCount} estabelecimento(s)` : "-"}
                            </span>
                        </span>
                    </Row>
                </div>          
            </Col>
            <Col className="leadCardCol" >
                <Row>
                    <span className= "leadCardTitle">Porte <br/>
                        <HighlightableText text={porteEmpresa[lead.porte]} highlight={searchQuery} />
                    </span>
                </Row>
                <Row>
                    <span className= "leadCardTitle">Capital Social <br/>
                        <HighlightableText text={`R$ ${lead.capital_social}`} highlight={searchQuery} />
                    </span>
                </Row>
                <Row> 
                    <Tooltip
                        title={`${formatCnae(lead.cnae_principal)}: ${subclasseSelect.find((element: any) => element.value === lead.cnae_principal)?.label || "Código não encontrado"}`}
                    >
                        <span className= "leadCardTitle">CNAE <br/>
                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                            <HighlightableText text={formatCnae(lead.cnae_principal)} highlight={searchQuery} />
                            <CiCircleInfo style={{ marginLeft: "0.5em" }}/>
                        </div>
                        </span>
                    </Tooltip>
                </Row>
            </Col>
            <Col className="leadCardCol" >
                <Row>
                    <span className= "leadCardTitle">Sócios <br/>
                        <Tooltip title={sociosList.length > 3 ? <span style={{ width: '50vh' }}>{sociosList.map((socio) => (<span>{socio}<br /></span>))}</span> : ''} overlayStyle={{ maxWidth: "50vh", whiteSpace: "normal" }}>
                            <span className="leadCardData" style={{overflowY:'auto', height:'5em'}}>
                                {sociosList.map((socio, index) => (index < 3 ? <span><HighlightableText text={socio} highlight={searchQuery} /> <br /></span> : <></>))}
                                {sociosList.length > 3 ? <span style={{ color: '#BFBFBF', fontSize: 'small' }}>Ver mais Sócios</span> : ''}
                            </span>
                        </Tooltip>
                    </span>
                </Row>
                <Row>
                    <Tooltip title={lead.email}>
                        <span className= "leadCardTitle">E-mail<br/>
                            <HighlightableText text={lead.email} highlight={searchQuery} />
                        </span>
                    </Tooltip>
                </Row>
                <Row>
                    <span className= "leadCardTitle">Telefone <br/>
                        <HighlightableText text={formatPhone(lead.telefone)} highlight={searchQuery} />
                    </span>
                </Row>
                <Row style={{display:'flex', alignItems:'center',marginTop:'0.5em'}}>
                    <Icons
                    setSelectedRecord={setSelectedRecord}
                    userPermissions={userPermissions}
                    setApiResponse={setApiResponse}
                    setModalLoading={setModalLoading}
                    setIsModalVisible={setIsModalVisible}
                    setIsTechModalVisible={setIsTechModalVisible}
                    record={lead}
                    />            
                </Row>  
            </Col>
            {
            <Col style={{width:'8%', display:'flex', flexDirection:'column', justifyContent:'space-between'}}>
                    <Row style={{display:'flex', alignItems:'center', justifyContent: 'space-around', marginTop: '1em'}}>
                        {
                            connection ? 
                            <div>
                                {sendToCrmButton(false, lead)}
                                {sendToAivButton(false, lead)}      
                                {sendToAdsButton(false, lead)}                  
                            </div>
                            :
                            <Tooltip title="Selecione uma conexão"><ConnectionButtonIcon/></Tooltip>
                        }

                        <Button
                            style={{marginLeft:'0.5em'}}
                            type="text"
                            danger
                            onClick={() => {
                            posthog.capture('lead_delete_button');
                            setLeadToDelete({razao_social: lead.razao_social,cnpj: lead.cnpj_completo, nome_lista: localStorage.getItem("leadId") ?? ''})
                            setIsDeleteModalVisible(true)
                            }}
                            icon={<DeleteIcon />}
                        />   
                    </Row>     
            </Col>
            }
                <Row style={{ width: '100%', display: 'flex', flexDirection:'row', justifyContent: 'space-between'}}>
                    <div style={{width:'23%', backgroundColor:'#E6E6F1', borderRadius: '0 0 0 30px'}}></div>
                    <div style={{display:'flex', justifyContent:'space-around',paddingBottom:'1em', paddingRight:'2em'}}>
                        <Tooltip title={lead.dominio_email && userPermissions.ops_enriquecimento ? lead.dados_empresa ? 'Ver empresa' : 'Enriquecer empresa' : 'Não disponível'}>
                            <Button className="submitButton"
                                style={lead.dominio_email && haveEnrichmentCredits && userPermissions.ops_enriquecimento ? lead.dados_empresa ? {cursor:'pointer'} : {cursor:'pointer'}  : {color:'gray'}} 
                                onClick={()=>{
                                    posthog.capture('enriquecer_empresa_coluna_botao');
                                    if(!userPermissions.ops_enriquecimento || !lead.dominio_email || !haveEnrichmentCredits) return
                                    setDomainToEnrich(lead.dominio_email)
                                    setEnrichmentModalVisible(true)
                                    setSelectedRecord(lead)                         
                                }}
                            >
                                Enriquecer
                            </Button>
                        </Tooltip>
                    </div>  
                </Row> 
        </Row>
    )
}

export default LeadCard